import React, { useCallback, useEffect, useRef, useState } from 'react'
import { css } from '@linaria/core'
import { MarginBox, Separator, colors } from '@fs/zion-ui'
import { QuickGlanceOverlayBase } from '@fs/zion-ui/quarks-authorized-use-only'
import { useResizeObserver, useUnmountableReducer } from '@fs/zion-frontend-friends'
import { useAutosuggestContext } from './AutosuggestProvider'
import { AutosuggestList } from './AutosuggestList'
import { privateProps } from './AutosuggestState'

const overlayCss = css`
  background: ${colors.gray00};
  width: calc(100% - 4px); // todo: undo this band-aid when we redesign the overlay to be connected to the input
`

const scrollerCss = css`
  max-height: 400px;
  overflow-y: auto;
  overflow-x: clip;
`

const loadingCss = css`
  position: relative;
  height: 6px;
  background: ${colors.gray03};

  &::after {
    content: '';
    background: ${colors.gray20};
    position: absolute;
    inset: 0;
    width: 50%;
    height: 100%;
    animation: slidyTimes 2.5s infinite;
    border-radius: 800px;
  }
  @keyframes slidyTimes {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(200%);
    }
  }
`

type OverlayState = { isOpen: boolean; transitioning: boolean }
type OverlayAction = boolean | 'TRANSITIONED'

const defaultOverlayState = { isOpen: false, transitioning: false }

const OverlayReducer = (state: OverlayState, action: OverlayAction): OverlayState => {
  if (action === 'TRANSITIONED') {
    if (!state.transitioning) return state
    return { ...state, transitioning: false }
  }
  if (state.isOpen === action) return state
  return { isOpen: action, transitioning: true }
}

const noOp = (): void => undefined

export default function AutosuggestOverlay({ inputRef, anchorRef }): JSX.Element {
  const {
    isLoading,
    suggestions,
    lastResort,
    [privateProps]: { listIsShowing, focusedIndex },
  } = useAutosuggestContext().config

  const [{ isOpen, transitioning }, dispatch] = useUnmountableReducer(OverlayReducer, defaultOverlayState)

  const handleTransitioned = useCallback(() => dispatch('TRANSITIONED'), [dispatch])
  useEffect(
    () => dispatch(listIsShowing && (isLoading || suggestions.length > 0 || !!lastResort)),
    [listIsShowing, isLoading, suggestions.length, lastResort, dispatch]
  )

  const [width, setWidth] = useState<string | number>('100%')
  const resizeCallback = useCallback(() => setWidth(anchorRef.current.getBoundingClientRect().width), [anchorRef])
  useResizeObserver(anchorRef, resizeCallback)

  const scrollRef = useRef<HTMLDivElement>(null)
  const focusedRef = useRef<HTMLLIElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  // Scrolls and alters input display when a new item is selected
  useEffect(() => {
    if (focusedIndex === -1 && scrollRef.current) {
      inputRef?.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
      })
      scrollRef.current.scrollTop = 0
    } else {
      focusedRef?.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [focusedIndex, inputRef])

  return (
    <QuickGlanceOverlayBase
      isOpen={isOpen}
      transitioning={transitioning}
      close={noOp} // Not needed, because blurring always closes the overlay
      handleTransitioned={handleTransitioned}
      placement={['bottom', 'top']}
      width={width}
      targetRef={inputRef}
      focusBackRef={inputRef}
      overlayRef={overlayRef}
      escapeToClose={false}
      className={overlayCss}
      z={2} // make it more connected to the input visually
    >
      <MarginBox marginX="xxs" marginY="none" ref={scrollRef} className={scrollerCss} style={{ width }}>
        <AutosuggestList focusedRef={focusedRef} />
      </MarginBox>
      {isLoading && suggestions.length === 0 && !lastResort && <Separator size="xl" />}
      {isLoading && <div className={loadingCss} />}
    </QuickGlanceOverlayBase>
  )
}
